import React, { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useRender } from '@base-ui-components/react/use-render';
import { ChevronDown } from 'lucide-react';
import { cn } from '@ui/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-4 font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-level-1',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-20',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-button-primary-bg text-button-primary-text',
          'hover:bg-button-primary-bg-hover',
          'disabled:bg-button-primary-bg-disabled disabled:text-button-primary-text-disabled disabled:pointer-events-none',
        ],
        secondary: [
          'bg-button-secondary-bg text-button-secondary-text',
          'border border-button-secondary-border',
          'hover:bg-button-secondary-bg-hover hover:border-button-secondary-border-hover',
          'disabled:bg-button-secondary-bg-disabled disabled:border-button-secondary-border-disabled disabled:text-button-secondary-text-disabled disabled:pointer-events-none',
        ],
        tertiary: [
          'bg-transparent text-button-tertiary-text',
          'hover:bg-button-tertiary-bg-hover',
          'disabled:text-button-tertiary-text-disabled disabled:pointer-events-none',
        ],
      },
      size: {
        standard: 'h-32 px-16 text-reg leading-24 tracking-[0.25px] rounded-lg',
        small: 'h-28 px-12 text-sm leading-24 tracking-[0.25px] rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'standard',
    },
  }
);

function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin size-16', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Show a loading spinner and disable interaction */
  loading?: boolean;
  /** Render as a square icon-only button */
  iconOnly?: boolean;
  /** Show a split dropdown section with a chevron */
  split?: boolean;
  /** Icon element to render before the label */
  iconLeading?: React.ReactNode;
  /** Icon element to render after the label */
  iconTrailing?: React.ReactNode;
  /** Click handler for the split dropdown section */
  onSplitClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Render as a different element (e.g. `<a href="...">`) for polymorphism */
  render?: React.ReactElement<Record<string, unknown>>;
}

/**
 * A button with primary, secondary, and tertiary variants, optional icons, and split-button support.
 *
 * @see {@link https://www.figma.com/design/DMtZ2xoJhsy1gKZAJvRDVA/Sirona-Medical-Design-System?node-id=5656-33744&m=dev Figma}
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'standard',
    loading = false,
    iconOnly = false,
    split = false,
    iconLeading,
    iconTrailing,
    onSplitClick,
    render,
    disabled,
    children,
    ...props
  },
  forwardedRef
) {
  const isDisabled = disabled || loading;

  const content = loading ? (
    <Spinner />
  ) : (
    <>
      {iconLeading}
      {!iconOnly && children}
      {iconTrailing}
    </>
  );

  const buttonClassName = cn(
    buttonVariants({ variant, size }),
    iconOnly && size === 'standard' && 'w-32 px-0',
    iconOnly && size === 'small' && 'w-28 px-0 [&_svg]:size-16',
    split && 'rounded-r-none border-r-0',
    className
  );

  // useRender must be called unconditionally (React hooks rule).
  // For split buttons we only use the rendered element for the main action button.
  // eslint-disable-next-line testing-library/render-result-naming-convention -- Base UI useRender, not testing-library render
  const buttonNode = useRender({
    render: split ? undefined : render,
    ref: forwardedRef,
    state: { disabled: isDisabled, loading } as Record<string, unknown>,
    props: {
      ...props,
      disabled: isDisabled,
      className: buttonClassName,
      'aria-busy': loading || undefined,
      'aria-disabled': isDisabled || undefined,
      children: content,
    },
    defaultTagName: 'button',
  });

  if (split) {
    const dividerColor =
      variant === 'primary'
        ? 'border-l-neutral-alpha-light-a32'
        : 'border-l-button-secondary-border';

    const splitClasses = cn(
      buttonVariants({ variant, size }),
      'rounded-l-none px-10 border-l',
      dividerColor
    );

    return (
      <div role="group" className="inline-flex">
        <button
          ref={forwardedRef}
          className={buttonClassName}
          disabled={isDisabled}
          aria-busy={loading || undefined}
          aria-disabled={isDisabled || undefined}
          {...props}
        >
          {content}
        </button>
        <button
          className={splitClasses}
          disabled={isDisabled}
          onClick={onSplitClick}
          aria-label="More options"
        >
          <ChevronDown className="size-16" />
        </button>
      </div>
    );
  }

  return buttonNode;
});

Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
